<template>
  <div class="app-layout">
    <Sidebar />
    <div class="create-user-container">
      <div class="header">
        <router-link to="/users" class="back-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Users
        </router-link>
        <h1>Create New User</h1>
      </div>

      <div class="content">
        <form @submit.prevent="handleSubmit" class="create-user-form">
          <div class="form-group">
            <label for="firstName">First Name</label>
            <input 
              id="firstName"
              v-model="form.first_name"
              type="text"
              required
              class="form-input"
              :class="{ 'error': errors.first_name }"
            />
            <span v-if="errors.first_name" class="error-message">{{ errors.first_name }}</span>
          </div>

          <div class="form-group">
            <label for="lastName">Last Name</label>
            <input 
              id="lastName"
              v-model="form.last_name"
              type="text"
              required
              class="form-input"
              :class="{ 'error': errors.last_name }"
            />
            <span v-if="errors.last_name" class="error-message">{{ errors.last_name }}</span>
          </div>

          <div class="form-group">
            <label for="email">Email</label>
            <input 
              id="email"
              v-model="form.email"
              type="email"
              required
              class="form-input"
              :class="{ 'error': errors.email }"
            />
            <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input 
              id="password"
              v-model="form.password"
              type="password"
              required
              class="form-input"
              :class="{ 'error': errors.password }"
            />
            <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
          </div>

          <div class="form-group">
            <label for="passwordConfirmation">Confirm Password</label>
            <input 
              id="passwordConfirmation"
              v-model="form.password_confirmation"
              type="password"
              required
              class="form-input"
              :class="{ 'error': errors.password_confirmation }"
            />
            <span v-if="errors.password_confirmation" class="error-message">{{ errors.password_confirmation }}</span>
          </div>

          <div class="form-actions">
            <button type="submit" class="submit-button" :disabled="loading">
              <span v-if="loading">Creating...</span>
              <span v-else>Create User</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'CreateUser',
  components: {
    Sidebar
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const errors = reactive({});
    const form = reactive({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: ''
    });

    const handleSubmit = async () => {
      loading.value = true;
      errors.value = {};

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        await axios.post('https://api.kewaar.com/api/admin/users', form, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        router.push('/users');
      } catch (err) {
        if (err.response?.data?.errors) {
          Object.assign(errors, err.response.data.errors);
        } else {
          errors.general = err.message || 'An error occurred while creating the user';
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      errors,
      loading,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
}

.create-user-container {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
}

.header {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4f46e5;
  text-decoration: none;
  font-weight: 500;
}

.back-button svg {
  width: 20px;
  height: 20px;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
}

.create-user-form {
  max-width: 600px;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #1a1a1a;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.form-input.error {
  border-color: #dc2626;
}

.error-message {
  display: block;
  margin-top: 0.5rem;
  color: #dc2626;
  font-size: 0.875rem;
}

.form-actions {
  margin-top: 2rem;
}

.submit-button {
  padding: 0.75rem 1.5rem;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-button:hover {
  background: #4338ca;
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .create-user-container {
    margin-left: 0;
  }
}
</style> 